import React, { useState } from 'react';
import { Button, makeStyles, FormControl, FormLabel, Typography, TextField, MenuItem, Checkbox, ListItemText } from '@material-ui/core';
import Dialog from '../ui/Dialog';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay.component';
import { getProgramsList } from '../../services/program.services';
import { updateUserInfo } from '../../services/user.service';
import CreateIcon from '@material-ui/icons/Create';

const EnrolledPharmaciesSelectPrograms = ({ data }) => {
  const { data:userData } = data;
  const {program_id, pharmacy_id:pharmacyId,metadata} = userData
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmation, setConfirmation] = useState(false);
  const [modalTitle, setModalTitle] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(JSON.parse(userData.program_list)||[]);
  const [programOptions, setProgramOptions] = useState([]);
  const [isProgramsLoading, setProgramsLoading] = useState(false);

  const useStyles = makeStyles(() => ({
    field: {
      width: '100%',
    },
    formControl: {
      width: '600px'
    },
    dialogPaper: {
      width: 550,
      maxWidth: 550,
      minHeight: 580,
    },
  }));
  const classes = useStyles();
  
  const handleClickAction = () => {
    setOpenModal(true);
    getPrograms();
    setModalTitle('Select Program`s');
  }

  const handleClose = () => {
    setOpenModal(false);
    setConfirmation(false);
    setErrorMessage(null);
    setSelectedProgram([]);
    if (openConfirmation) {
      window.location.reload();
    }
  }
 
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const resolvedProgramId = program_id || window?.programConfig?.program_id;
      if (!resolvedProgramId) {
        throw new Error('Missing program_id');
      }
      if (!pharmacyId) {
        throw new Error('Missing user id');
      }

      const selectedProgramList = Array.isArray(selectedProgram) ? selectedProgram : [];     

      const selectedProgramNames = selectedProgramList
        .map(id => programOptions.find(p => p.value === id)?.label)
        .filter(Boolean);
      const selectedProgramsText = selectedProgramNames.join(', ');

      // Persist to user metadata so reporting SQL can read it for "Selected Programs" column.
      await updateUserInfo({
         program_id:resolvedProgramId,
         id: pharmacyId,
        payload: {
          ...userData,
         mobile_number: userData.mobile_number,
          metadata: {           
            ...metadata,
            selected_programs: selectedProgramsText,
            selected_programs_list: selectedProgramList,
            
          },
         }
      });

      setSuccessMessage('Selected programs updated successfully.');
      setConfirmation(true);
    } catch(err) {
      setErrorMessage(err?.message || 'Unable to update selected programs');
    } finally {
      setLoading(false);
    }
  }
  const getPrograms = async () => {
    try {
      setProgramsLoading(true);
      setErrorMessage(null);

      const parentProgramId = window?.programConfig?.program_id || "";

      const result = await getProgramsList();

      const list = Array.isArray(result) ? result : [];
      const filtered = parentProgramId
        ? list.filter((program) => program?.program_metadata?.parent_program_id === parentProgramId)
        : list;

      // Display only program.program_id in dropdown
      setProgramOptions(
        filtered
          .map((program) => {
            const id = program?.program_name;
            return id ? { value: String(program?.program_id), label: String(id) } : null;
          })
          .filter(Boolean)
      );
    } catch (e) {
      setProgramOptions([]);
      setErrorMessage('Unable to load programs');
    } finally {
      setProgramsLoading(false);
    }
  }

  const formContent = (
    <>
        <FormControl className={classes.field}>
          <FormLabel className={classes.field}>
            <Typography>{modalTitle}</Typography>
          </FormLabel>

          <TextField
            select
            variant="outlined"
            size="small"
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            fullWidth
            disabled={isProgramsLoading || !programOptions.length}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              MenuProps: {
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                PaperProps: {
                  style: { maxHeight: 320 },
                },
              },
              // Don't show selected program names in the input itself
              renderValue: (selected) => {
                const count = Array.isArray(selected) ? selected.length : 0;
                return count ? `${count} selected` : 'Select program(s)';
              },
            }}
          >
            {isProgramsLoading ? (
              <MenuItem value="" disabled>
                Loading...
              </MenuItem>
            ) : programOptions.length ? (
              programOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox checked={selectedProgram.indexOf(o.value) > -1} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No programs found
              </MenuItem>
            )}
          </TextField>  
      
        </FormControl>
  
      <Typography
          variant='caption'
          display='block'
          gutterBottom
          color='error'
      >
          { errorMessage }
      </Typography>
    </>
  );

  const formButtons = (
    <>
        <Button onClick={handleClose} variant='contained'>
            Close
        </Button>
        { !openConfirmation && 
        <Button onClick={onSubmit} variant='contained' color='secondary'>
            Submit
        </Button> }
    </>
  );

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <>
     <Dialog 
      key='select-programs'
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
      open={openModal} 
      title = {modalTitle}
      content = {formContent}
      children = {formButtons}
      onSubmit={handleClickAction}
      onClose={handleClose}
      />
     <Dialog 
      key='confirmation'
      open={openConfirmation}
      title = 'Confirmation'
      content = {successMessage}
      children = {formButtons}
      onSubmit={handleClose}
      onClose={handleClose}
      />
     <Button variant="contained" color='primary' onClick={handleClickAction} style={{ marginRight: 8 }}>
      <CreateIcon></CreateIcon>
     </Button>
    </>
  )
};

export default EnrolledPharmaciesSelectPrograms;